import { type ApiContainerStockEntry, type LinkableField } from '@Model';
import { type ContainerStockRow } from './containerStockGroups';

/** The standing time id of a line that does not exist yet, which the server does not read. */
const NO_STANDING_TIME = '00000000-0000-0000-0000-000000000000';

/** The fields of a stock line as the correction dialog edits them. */
export interface ContainerStockDraft {
    containerType: LinkableField;
    /** The type conditions look at; the server keeps the stored one while it is unset. */
    billedAs: LinkableField;
    container: LinkableField;
    site: LinkableField;
    customer: LinkableField;
    content: LinkableField;
    remarks: string;
    standingSince: string;
    /** Empty while the containers still stand. */
    endedAt: string;
}

/** The resource name each link of a stock line points to. */
export const StockResource = {
    ContainerType: 'container_types',
    Container: 'containers',
    Address: 'addresses',
    Service: 'services',
} as const;

/** A link that names nothing. */
export function unlinked(resource: string): LinkableField {
    return { foreign_key: undefined, display_string: '', api_resource_name: resource };
}

/** An empty form, for a container that stands without a booked action. */
// @ai-generated 2026-10-01T15:01:25+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function emptyDraft(now: Date): ContainerStockDraft {
    return {
        containerType: unlinked(StockResource.ContainerType),
        billedAs: unlinked(StockResource.ContainerType),
        container: unlinked(StockResource.Container),
        site: unlinked(StockResource.Address),
        customer: unlinked(StockResource.Address),
        content: unlinked(StockResource.Service),
        remarks: '',
        standingSince: now.toISOString(),
        endedAt: '',
    };
}

/** The form for an existing line, filled with what the stock shows. */
// @ai-generated 2026-10-01T15:01:25+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function draftFromRow(row: ContainerStockRow): ContainerStockDraft {
    return {
        containerType: row.container_type,
        billedAs: row.billed_as,
        container: row.container,
        site: row.site,
        customer: row.customer,
        content: row.content,
        remarks: row.remarks,
        standingSince: row.standing_since,
        endedAt: row.ended_at ?? '',
    };
}

/** The form with another container type, without the container if that belongs to the type before. */
// @ai-generated 2026-10-02T12:10:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function withContainerType(draft: ContainerStockDraft, containerType: LinkableField): ContainerStockDraft {
    const sameType = containerType.foreign_key === draft.containerType.foreign_key;

    return { ...draft, containerType, container: sameType ? draft.container : unlinked(StockResource.Container) };
}

/** Whether a row stands for a container of a pool, which the stock names by no number. */
export function isPoolRow(row: ContainerStockRow | undefined): boolean {
    return row !== undefined && !row.container.foreign_key;
}

/** Which fields still have to be filled before the line can be saved. */
// @ai-generated 2026-10-01T15:01:25+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function missingFields(draft: ContainerStockDraft, pool: boolean): string[] {
    const required: [string, boolean][] = [
        ['Containertyp', !!draft.containerType.foreign_key],
        ['Nummer', pool || !!draft.container.foreign_key],
        ['Standort', !!draft.site.foreign_key],
        ['Kunde', !!draft.customer.foreign_key],
        ['Steht seit', draft.standingSince !== ''],
    ];

    return required.filter(([, filled]) => !filled).map(([name]) => name);
}

/** The stock line as it should be, in the shape the server takes. */
// @ai-generated 2026-10-01T15:01:25+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function entryFromDraft(draft: ContainerStockDraft, row: ContainerStockRow | undefined): ApiContainerStockEntry {
    return {
        container_type: draft.containerType,
        billed_as: draft.billedAs,
        container: draft.container,
        standing_since: draft.standingSince,
        ended_at: draft.endedAt === '' ? undefined : draft.endedAt,
        site: draft.site,
        customer: draft.customer,
        content: draft.content,
        remarks: draft.remarks,
        standing_time_id: row?.standing_time_id ?? NO_STANDING_TIME,
        allowed_follow_ups: [],
    };
}
